// frontend/src/pages/AdminUsers.js – NextScenes Admin: User Management (Actions)
// Build: 2026-04-26-admin-users-registered-date-v1
//
// Canonical endpoints (admin only):
// - GET    /api/admin/users?page=1&limit=20
// - PATCH  /api/admin/users/:id/role    { role: "user"|"writer"|"admin" }
// - PATCH  /api/admin/users/:id/active  { isActive: true|false }
// - PATCH  /api/admin/users/verify      { email, emailVerified, notifyUser?, notifyMessage? }
// - DELETE /api/admin/users/:id

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { api } from "../api";
import { useUser } from "../UserContext";

function safeStr(v) {
  return typeof v === "string" ? v : String(v || "");
}

function roleLabel(role) {
  const r = safeStr(role).trim().toLowerCase();
  if (r === "power") return "writer";
  if (r === "admin") return "admin";
  if (r === "moderator") return "moderator";
  if (r === "writer") return "writer";
  return "user";
}

function fmtBool(v) {
  return v ? "Yes" : "No";
}

function fmtDateTime(v) {
  if (!v) return "—";

  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";

  try {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d.toLocaleDateString();
  }
}

function clampInt(n, lo, hi) {
  const x = Number(n);
  if (!Number.isFinite(x)) return lo;
  return Math.max(lo, Math.min(hi, Math.trunc(x)));
}

function pillStyle(kind) {
  const base = {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    border: "1px solid rgba(15, 61, 46, 0.18)",
    background: "rgba(16, 185, 129, 0.08)",
    color: "#0f3d2e",
    whiteSpace: "nowrap",
  };

  const k = String(kind || "").toLowerCase();

  if (k === "admin") return { ...base, background: "rgba(245, 158, 11, 0.14)", color: "#7c2d12" };
  if (k === "writer") return { ...base, background: "rgba(59, 130, 246, 0.12)", color: "#1e3a8a" };
  if (k === "moderator") return { ...base, background: "rgba(99, 102, 241, 0.12)", color: "#312e81" };
  if (k === "user") return { ...base, background: "rgba(148, 163, 184, 0.18)", color: "#334155" };

  if (k === "active") return { ...base, background: "rgba(34, 197, 94, 0.12)", color: "#14532d" };
  if (k === "disabled") return { ...base, background: "rgba(239, 68, 68, 0.10)", color: "#7f1d1d" };

  return base;
}

async function apiGet(path) {
  if (api && typeof api.get === "function") return await api.get(path);
  return await api(path, { method: "GET" });
}

async function apiPatch(path, body) {
  if (api && typeof api.patch === "function") return await api.patch(path, body);
  return await api(path, { method: "PATCH", body: JSON.stringify(body) });
}

async function apiDel(path) {
  if (api && typeof api.del === "function") return await api.del(path);
  if (api && typeof api.delete === "function") return await api.delete(path);
  return await api(path, { method: "DELETE" });
}

export default function AdminUsers({ onNavigate }) {
  const { user: me, refreshMe } = useUser();
  const myRole = safeStr(me?.role).trim().toLowerCase();
  const isAdmin = myRole === "admin";

  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  function clearFlash() {
    setErr("");
    setOk("");
  }

  function upsertUser(updated) {
    if (!updated || !updated.id) return;
    setUsers((prev) => {
      const list = Array.isArray(prev) ? prev.slice() : [];
      const idx = list.findIndex((x) => String(x?.id) === String(updated.id));
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...updated };
        return list;
      }
      return [updated, ...list];
    });
  }

  function removeUser(id) {
    setUsers((prev) => (Array.isArray(prev) ? prev.filter((u) => String(u?.id) !== String(id)) : []));
  }

  async function load(p = page, l = limit) {
    clearFlash();
    setLoading(true);

    const pageSafe = clampInt(p, 1, 1000000);
    const limitSafe = clampInt(l, 5, 100);

    try {
      const res = await apiGet(`/api/admin/users?page=${pageSafe}&limit=${limitSafe}`);

      const list = Array.isArray(res?.users)
        ? res.users
        : Array.isArray(res?.items)
        ? res.items
        : [];

      setUsers(list);

      const tp = clampInt(res?.totalPages, 1, 1000000);
      const ti = clampInt(res?.totalItems, 0, 1000000000);

      setTotalPages(tp);
      setTotalItems(ti);

      setPage(clampInt(res?.page || pageSafe, 1, tp));
      setLimit(clampInt(res?.limit || limitSafe, 5, 100));
    } catch (e) {
      const m = safeStr(e?.data?.message) || safeStr(e?.message) || "Failed to load users.";
      setErr(m);
      setUsers([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isAdmin) return;
    setPage(1);
    setLimit(20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    load(page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, page, limit]);

  const filtered = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    const needle = safeStr(q).trim().toLowerCase();
    if (!needle) return list;

    return list.filter((u) => {
      const email = safeStr(u?.email).toLowerCase();
      const username = safeStr(u?.username).toLowerCase();
      const name = safeStr(u?.name).toLowerCase();
      const role = safeStr(u?.role).toLowerCase();
      return email.includes(needle) || username.includes(needle) || name.includes(needle) || role.includes(needle);
    });
  }, [users, q]);

  const counts = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    let active = 0;
    let admins = 0;
    let writers = 0;
    let moderators = 0;

    list.forEach((u) => {
      const r = roleLabel(u?.role);
      const isActive = u?.isActive !== false;
      if (isActive) active += 1;
      if (r === "admin") admins += 1;
      if (r === "writer") writers += 1;
      if (r === "moderator") moderators += 1;
    });

    return { total: list.length, active, admins, writers, moderators };
  }, [users]);

  async function changeRole(u, nextRole) {
    const id = safeStr(u?.id).trim();
    if (!id) return;

    clearFlash();
    setBusyId(id);

    try {
      const res = await apiPatch(`/api/admin/users/${encodeURIComponent(id)}/role`, {
        role: safeStr(nextRole).toLowerCase(),
      });

      if (res?.user) {
        upsertUser(res.user);
        setOk(`Role updated: ${safeStr(res.user.email) || id} → ${roleLabel(res.user.role)}`);
      } else {
        await load(page, limit);
        setOk("Role updated.");
      }

      if (safeStr(me?.id) && String(me?.id) === String(id)) {
        await refreshMe();
      }
    } catch (e) {
      const m = safeStr(e?.data?.message) || safeStr(e?.message) || "Failed to update role.";
      setErr(m);
    } finally {
      setBusyId("");
    }
  }

  async function toggleContentSteward(u) {
    const id = safeStr(u?.id).trim();
    if (!id || busyId) return;
    const enabled = u?.contentSteward !== true;
    if (!window.confirm(enabled
      ? "Appoint this verified member as Content Steward? They can update Spotlight, Daily Words, Featured Reading and the weekly puzzle, but cannot manage accounts or moderation."
      : "Revoke this Content Steward appointment immediately?")) return;
    clearFlash();
    setBusyId(id);
    try {
      const res = await apiPatch(`/api/admin/users/${encodeURIComponent(id)}/content-steward`, {enabled});
      if (res?.user) upsertUser(res.user);
      setOk(enabled ? "Content Steward appointed." : "Content Steward appointment revoked.");
    } catch (e) {
      setErr(safeStr(e?.data?.message) || safeStr(e?.message) || "Could not update editorial appointment.");
    } finally { setBusyId(""); }
  }

  async function toggleActive(u) {
    const id = safeStr(u?.id).trim();
    if (!id) return;

    const next = !(u?.isActive !== false);

    clearFlash();
    setBusyId(id);

    try {
      const res = await apiPatch(`/api/admin/users/${encodeURIComponent(id)}/active`, {
        isActive: Boolean(next),
      });

      if (res?.user) {
        upsertUser(res.user);
        setOk(`User ${next ? "enabled" : "disabled"}: ${safeStr(res.user.email) || id}`);
      } else {
        await load(page, limit);
        setOk("Status updated.");
      }
    } catch (e) {
      const m = safeStr(e?.data?.message) || safeStr(e?.message) || "Failed to update status.";
      setErr(m);
    } finally {
      setBusyId("");
    }
  }

  async function setVerified(u, nextValue) {
    const id = safeStr(u?.id).trim();
    const email = safeStr(u?.email).trim().toLowerCase();
    if (!id || !email) return;

    let notifyUser = false;
    let notifyMessage = "";

    if (nextValue === true) {
      notifyUser = window.confirm("Verify this user and attempt to notify them by email?");
      if (notifyUser) {
        notifyMessage = "Your account has been verified. You can now sign in.";
      }
    }

    clearFlash();
    setBusyId(id);

    try {
      const res = await apiPatch("/api/admin/users/verify", {
        email,
        emailVerified: Boolean(nextValue),
        notifyUser,
        notifyMessage,
      });

      if (res?.user) {
        upsertUser(res.user);
        setOk(
          `${nextValue ? "Verified" : "Marked unverified"}: ${safeStr(res.user.email) || email}${
            nextValue && notifyUser
              ? res?.notify?.sent
                ? " • notification sent"
                : " • notification attempted"
              : ""
          }`
        );
      } else {
        await load(page, limit);
        setOk(nextValue ? "User verified." : "User marked as unverified.");
      }
    } catch (e) {
      const m = safeStr(e?.data?.message) || safeStr(e?.message) || "Failed to update verification.";
      setErr(m);
    } finally {
      setBusyId("");
    }
  }

  async function deleteUser(u) {
    const id = safeStr(u?.id).trim();
    const email = safeStr(u?.email) || id;
    if (!id) return;

    const yes = window.confirm(`Delete this account?\n\n${email}\n\nThis cannot be undone.`);
    if (!yes) return;

    clearFlash();
    setBusyId(id);

    try {
      await apiDel(`/api/admin/users/${encodeURIComponent(id)}`);
      removeUser(id);
      setOk(`Deleted: ${email}`);
    } catch (e) {
      const m = safeStr(e?.data?.message) || safeStr(e?.message) || "Failed to delete user.";
      setErr(m);
    } finally {
      setBusyId("");
    }
  }

  if (!isAdmin) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 820, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>User Management</h2>
          <p style={{ color: "#475569" }}>This page is restricted to Admin accounts.</p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
            <button className="btn" type="button" onClick={() => onNavigate && onNavigate("admin")}>
              Back to Dashboard
            </button>
            <button className="btn" type="button" onClick={() => onNavigate && onNavigate("home")}>
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pageLabel = `Page ${page} of ${totalPages} • ${totalItems} total`;

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div
          style={{
            padding: "12px 14px",
            borderRadius: 14,
            border: "1px solid rgba(15, 61, 46, 0.16)",
            background: "linear-gradient(135deg, rgba(16, 185, 129, 0.14), rgba(255, 255, 255, 0.92))",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <h2 style={{ marginTop: 0, marginBottom: 6, color: "#0f3d2e" }}>User Management (Actions)</h2>
              <div style={{ color: "#355b52", fontWeight: 800 }}>
                Promote, demote, disable, enable, verify, and delete accounts. Source of truth is the server.
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button className="btn" type="button" onClick={() => load(page, limit)} disabled={loading}>
                {loading ? "Loading..." : "Refresh"}
              </button>
              <button className="btn" type="button" onClick={() => onNavigate && onNavigate("admin")}>
                Back to Dashboard
              </button>
              <button className="btn" type="button" onClick={() => onNavigate && onNavigate("home")}>
                Go Home
              </button>
            </div>
          </div>

          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button className="btn" type="button" disabled={loading || page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Prev
              </button>

              <div style={{ fontSize: 13, fontWeight: 900, color: "#0f3d2e" }}>{pageLabel}</div>

              <button
                className="btn"
                type="button"
                disabled={loading || page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: "#0f3d2e" }}>Per page</div>
              <select
                value={limit}
                onChange={(e) => {
                  const v = clampInt(e.target.value, 5, 100);
                  setPage(1);
                  setLimit(v);
                }}
                style={{
                  padding: "8px 10px",
                  borderRadius: 12,
                  border: "1px solid rgba(15, 61, 46, 0.22)",
                  fontWeight: 900,
                  background: "#fff",
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {err ? (
          <div className="banner" style={{ marginTop: 12, borderLeft: "6px solid #dc2626" }}>
            {err}
          </div>
        ) : null}
        {ok ? (
          <div className="banner" style={{ marginTop: 12, borderLeft: "6px solid #16a34a" }}>
            {ok}
          </div>
        ) : null}

        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <span style={pillStyle("user")}>On this page: {counts.total}</span>
          <span style={pillStyle("active")}>Active: {counts.active}</span>
          <span style={pillStyle("writer")}>Writers: {counts.writers}</span>
          <span style={pillStyle("moderator")}>Moderators: {counts.moderators}</span>
          <span style={pillStyle("admin")}>Admins: {counts.admins}</span>

          <div style={{ marginLeft: "auto", minWidth: 280, width: 380, maxWidth: "100%" }}>
            <input
              className="ns-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email, username, name, role..."
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                outline: "none",
                fontWeight: 800,
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.35fr 0.9fr 0.9fr 0.95fr 0.75fr 0.8fr 1.45fr",
              gap: 10,
              padding: "10px 12px",
              background: "rgba(16, 185, 129, 0.08)",
              borderBottom: "1px solid #e5e7eb",
              fontSize: 12,
              fontWeight: 900,
              color: "#0f172a",
              alignItems: "center",
            }}
          >
            <div>Email</div>
            <div>Username</div>
            <div>Name</div>
            <div>Registered</div>
            <div>Role</div>
            <div>Status</div>
            <div>Actions</div>
          </div>

          {loading ? (
            <div style={{ padding: 14, color: "#475569", fontWeight: 900 }}>Loading users…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 14, color: "#475569", fontWeight: 900 }}>No users found.</div>
          ) : (
            filtered.map((u, idx) => {
              const id = safeStr(u?.id) || `row_${idx}`;
              const isBusy = busyId === id;

              const email = safeStr(u?.email) || "(no email)";
              const username = safeStr(u?.username);
              const name = safeStr(u?.name);
              const role = roleLabel(u?.role);
              const active = u?.isActive !== false;
              const verified = u?.emailVerified === true;
              const registeredAt = fmtDateTime(u?.createdAt);

              return (
                <div
                  key={id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.35fr 0.9fr 0.9fr 0.95fr 0.75fr 0.8fr 1.45fr",
                    gap: 10,
                    padding: "10px 12px",
                    borderBottom: "1px solid #f1f5f9",
                    alignItems: "center",
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 900, color: "#0f172a" }}>
                    {email}
                    <div style={{ marginTop: 2, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
                      Verified: {fmtBool(verified)}
                    </div>
                  </div>

                  <div style={{ fontWeight: 900, color: "#0f172a" }}>{username}</div>
                  <div style={{ fontWeight: 800, color: "#334155" }}>{name}</div>

                  <div style={{ fontWeight: 800, color: "#475569", fontSize: 12, lineHeight: 1.35 }}>
                    {registeredAt}
                  </div>

                  <div>
                    <select
                      value={role}
                      disabled={isBusy}
                      onChange={(e) => changeRole(u, e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: 12,
                        border: "1px solid #e5e7eb",
                        fontWeight: 900,
                        background: "#fff",
                        cursor: isBusy ? "not-allowed" : "pointer",
                      }}
                    >
                      <option value="user">user</option>
                      <option value="writer">writer</option>
                      <option value="moderator">moderator</option>
                      <option value="admin">admin</option>
                    </select>
                  </div>

                  <div>
                    <span style={pillStyle(active ? "active" : "disabled")}>{active ? "Active" : "Disabled"}</span>
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {role !== "admin" && <button className="btn" type="button"
                      disabled={isBusy || (u.contentSteward !== true && (!verified || !active))}
                      onClick={() => toggleContentSteward(u)}>
                      {u.contentSteward === true ? "Revoke Steward" : "Appoint Steward"}
                    </button>}
                    <button className="btn" type="button" onClick={() => toggleActive(u)} disabled={isBusy}>
                      {isBusy ? "Working..." : active ? "Disable" : "Enable"}
                    </button>

                    <button className="btn" type="button" onClick={() => setVerified(u, !verified)} disabled={isBusy}>
                      {isBusy ? "Working..." : verified ? "Unverify" : "Verify"}
                    </button>

                    <button className="btn btn-danger" type="button" onClick={() => deleteUser(u)} disabled={isBusy}>
                      {isBusy ? "Working..." : "Delete"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ marginTop: 12, color: "#64748b", fontWeight: 800, fontSize: 12 }}>
          Tip: Search filters the current page. Use Prev/Next to move through all users.
        </div>
      </div>
    </div>
  );
}
